// components
import { StyledFlexMatchContainer } from 'components/flex-portal/FlexMatch/FlexMatch.styles';
import { SingleOrRange } from 'components/shared/DateNavigation';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';
import { ToolbarFlowEnum } from 'components/shared/ToolbarSet/ToolbarWrapper.enums';
// import DutyChip from 'components/shared/ToolbarSet/components/DutyChipInToolBar'; // Note: Uncomment when duty chips are available
import { Icon } from '@sdb/planning-ui';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle
} from 'react-resizable-panels';
import RosterInfoPopover from './components/RosterInfoPopover';
import NoDataGridRows from './components/NoDataGridRows';
import CustomColumnMenu from './components/CustomColumnMenu';
import NoAccessPage from './components/NoAccessPage';

// constants
import { EMPTY_ROSTER_PLAN_FILTERS, FILTER_KEYS } from './RosterPlan.consts';

// Enums
import {
  UserPreferenceKeysEnums,
  UserPreferenceValueEnums
} from './RosterPlan.enums';
import { EmployeeType, SelfMatchAction } from 'components/enums';

// hooks
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetColumns } from './hooks/useGetColumns';
import useSyncHorizontalScroll from './hooks/useSyncHorizontalScroll';
import useGetOpenRequests from './hooks/useGetOpenRequests';
import useGetRosterPublishDate from './hooks/useGetRosterPublishDate';
import useGetEmployeesBasedOnCostCenters from './hooks/useGetEmployeesBasedOnCostCenters';
import useGetQualificationsByCostCenters from './hooks/useGetQualificationsByCostCenters';
import useSavePreferences from 'components/flex-portal/FlexMatch/hooks/useSavePreferences';
import { useSelector as useReduxSelector } from 'react-redux';
import { useSelector } from 'hooks';

// types
import type { PlanGroup, PlanUnitMix } from 'components/types';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  type GridColumnGroupingModel,
  type GridRowParams,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import type {
  EmployeeNameFormatTypes,
  RosterPlanFilter,
  RosterPlanFilterSelection,
  RosterPlanFiltersForm
} from './RosterPlan.types';
import type {
  Duty,
  EmployeeDutyData
} from 'RestAPIs/cost-centers/employees/types';
import type {
  DutyRequestItem,
  OpenRequestResponse
} from 'RestAPIs/roster/open-requests/types';
import type { RootState } from 'redux/rootReducer';

// styles
import {
  StyledPanelBox,
  StyledPanelsContainersBox,
  StyledResizeHandleBox,
  StyledResizeHandleDivider,
  StyledRosterPlanDataGridPro
} from './RosterPlan.styles';

// utils
import dayjs from 'dayjs';
import {
  anyOptionIdEqualsAsString,
  anyOptionLabelEquals,
  buildLabelSet,
  getUniqueFilterValues,
  normalizeToOptionArray
} from './RosterPlan.utils';
import { FormProvider, useForm } from 'react-hook-form';
import { FilterPopper } from 'components/flex-portal/FlexMatch/components/FilterPopper';
import { RosterPlanFilters } from './components/RosterPlanFilters';
import { v4 as uuidv4 } from 'uuid';

// Todo: save sorting modes in user preferences
// sortingOrder This is how it should be saved in user preferences
// [
//   {
//     key: 'rosterSorting',
//     value:
//       '[{"attribute":"nameCompound","descending":false,"realAttr":"nameCompound"}]'
//   }
// ];

// [
//   {
//     key: 'rosterSorting',
//     value:
//       '[{"attribute":"nameCompound","descending":true,"realAttr":"nameCompound"}]'
//   }
// ];

// [
//   {
//     key: 'rosterSorting',
//     value:
//       '[{"attribute":"nameCompound","descending":false,"realAttr":"sortorder"},{"attribute":"nameCompound","descending":false,"realAttr":"nameCompound"}]'
//   }
// ];

const RosterPlan = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });
  const { t: tCommon } = useTranslation('common');
  const { t: tHeader } = useTranslation('common', {
    keyPrefix: 'header'
  });

  const { hasReactRosterPlanViewAccess } = useSelector((state: RootState) => {
    return state.grants;
  });

  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const bottomPanelRef = useRef<ImperativePanelHandle>(null);
  const apiTop = useGridApiRef();
  const apiBottom = useGridApiRef();
  const [anchorElInfo, setAnchorElInfo] = useState<HTMLButtonElement | null>(
    null
  );
  const [appliedFilters, setAppliedFilters] = useState<
    RosterPlanFilterSelection[]
  >([]);

  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore((state) => state);
  const { period, setPeriod } = useDateNavigationStore((state) => state);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const costCenterGroups = selectedPlanUnits.filter(
    (unit) => typeof unit.uniqId === 'string' && unit.uniqId.includes('-group')
  );

  const selectedCostCenters = selectedPlanUnits
    .filter((unit) => typeof unit.uniqId === 'number')
    .map((unit) => unit.uniqId)
    .flat()
    .join(',');

  const costCenterGroupIds = costCenterGroups
    .map((group) =>
      Array.isArray((group as PlanGroup).costCenters)
        ? (group as PlanGroup).costCenters.map((cc) => cc.id)
        : []
    )
    .flat()
    .join(',');

  const allCostCentersIds = [selectedCostCenters, costCenterGroupIds]
    .filter((id) => id)
    .join(',');

  const startDate =
    period === 'fourWeeks'
      ? dayjs(selectedDate).startOf('week')
      : period === 'month'
      ? dayjs(selectedDate).startOf('month')
      : period === 'week'
      ? dayjs(selectedDate).startOf('week')
      : dayjs(selectedDate).startOf('day');
  const endDate =
    period === 'fourWeeks'
      ? dayjs(selectedDate).add(4, 'week').subtract(1, 'day')
      : period === 'month'
      ? dayjs(selectedDate).endOf('month')
      : period === 'week'
      ? dayjs(selectedDate).endOf('week')
      : dayjs(selectedDate).endOf('day');

  const { data: employees, isFetching: isFetchingEmployees } =
    useGetEmployeesBasedOnCostCenters(
      {
        costCenters: allCostCentersIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      },
      { enabled: !!allCostCentersIds && !!startDate && !!endDate }
    );

  const [employeesData, setEmployeesData] = useState<EmployeeDutyData[]>(
    employees || []
  );

  const { data: rosterPublishDate } = useGetRosterPublishDate(
    {
      costCenters: allCostCentersIds,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    },
    { enabled: !!allCostCentersIds && !!startDate && !!endDate }
  );

  const { data: openRequests, isFetching: isFetchingOpenRequests } =
    useGetOpenRequests(
      {
        costCenters: allCostCentersIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      },
      { enabled: !!allCostCentersIds && !!startDate && !!endDate }
    );

  const [openRequestsData, setOpenRequestsData] = useState<
    OpenRequestResponse[]
  >(openRequests || []);

  const { data: qualificationsByCostCenters } =
    useGetQualificationsByCostCenters(
      {
        costCenters: allCostCentersIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      },
      { enabled: !!allCostCentersIds && !!startDate && !!endDate }
    );

  const allDuties: Duty[] | undefined = employees
    ?.filter((employee) => employee.days?.length)
    .flatMap((employee) => employee.days)
    .flatMap((day) => day.duties)
    .filter((duty) => duty !== undefined);

  const getAllDutyHourTypes = (data: Duty[] | DutyRequestItem[]) => {
    return (
      data
        ?.map((duty) => duty?.periods)
        .flat()
        .map((period) => period?.dutyHourType)
        .filter((item) => item !== undefined) || []
    );
  };

  // Todo: Refactor later to keep file compact
  const filters: RosterPlanFilter[] = [
    {
      key: 'costCenter',
      options: getUniqueFilterValues({
        data: employees,
        labelKey: 'customerName',
        idKey: 'customerId'
      })
    },
    {
      key: 'function',
      options: getUniqueFilterValues({
        data: employees,
        labelKey: 'benefits.functionName',
        idKey: 'benefits.functionId'
      })
    },
    {
      key: 'employees',
      options: getUniqueFilterValues({
        data: employees,
        labelKey: 'nameInformal',
        idKey: 'id'
      })
    },
    {
      key: 'planned',
      options: [
        {
          label: t('filters.planned'),
          id: 'planned'
        },
        {
          label: t('filters.notPlanned'),
          id: 'notPlanned'
        }
      ]
    },
    {
      key: 'types',
      options: [
        {
          label: t(`filters.employeeType.${EmployeeType.OWN}`),
          id: EmployeeType.OWN
        },
        {
          label: t(`filters.employeeType.${EmployeeType.FLEX}`),
          id: EmployeeType.FLEX
        },
        {
          label: t(`filters.employeeType.${EmployeeType.BORROWING}`),
          id: EmployeeType.BORROWING
        },
        {
          label: t(`filters.employeeType.${EmployeeType.PREF}`),
          id: EmployeeType.PREF
        }
      ]
    },
    {
      key: 'qualifications',
      options: getUniqueFilterValues({
        data: qualificationsByCostCenters,
        labelKey: 'name',
        idKey: 'qualificationId'
      })
    },
    {
      key: 'dutyCodes',
      options: getUniqueFilterValues({
        data: allDuties,
        labelKey: 'displayDutyCode',
        idKey: 'dutyId'
      })
    },
    {
      key: 'dutyHourTypes',
      options: getUniqueFilterValues({
        data: getAllDutyHourTypes(allDuties || []),
        labelKey: 'code',
        idKey: 'id'
      })
    },
    {
      key: 'absences',
      options: [
        {
          label: t('filters.absencesPeriod'),
          id: 'absences'
        },
        {
          label: t('filters.leaveRequests'),
          id: 'leaveRequests'
        }
      ]
    },
    {
      key: 'costCenterOpenRequests',
      label: 'costCenter',
      section: 'openRequests',
      options: getUniqueFilterValues({
        data: openRequests,
        labelKey: 'costCenterName',
        idKey: 'costCenterId'
      })
    },
    {
      key: 'functionOpenRequests',
      label: 'function',
      section: 'openRequests',
      options: getUniqueFilterValues({
        data: openRequests,
        labelKey: 'functionName',
        idKey: 'functionId'
      })
    },
    {
      key: 'qualificationsOpenRequests',
      label: 'qualifications',
      section: 'openRequests',
      options: getUniqueFilterValues({
        data: qualificationsByCostCenters,
        labelKey: 'name',
        idKey: 'qualificationId'
      })
    },
    {
      key: 'dutyCodesOpenRequests',
      label: 'dutyCodes',
      section: 'openRequests',
      options: getUniqueFilterValues({
        data: openRequests,
        labelKey: 'displayDutyCode',
        idKey: 'dutyId'
      })
    },
    {
      key: 'dutyHourTypesOpenRequests',
      label: 'dutyHourTypes',
      section: 'openRequests',
      options: getUniqueFilterValues({
        data: getAllDutyHourTypes(openRequests || []),
        labelKey: 'code',
        idKey: 'id'
      })
    },
    {
      key: 'statusOpenRequests',
      label: 'status',
      section: 'openRequests',
      options: [
        {
          label: t('filters.open'),
          id: 'open'
        },
        {
          label: t('filters.sendToFlex'),
          id: 'sendToFlex'
        },
        {
          label: t('filters.openForSelfMatch'),
          id: 'openForSelfMatch'
        },
        {
          label: t('filters.subscribeSelfMatch'),
          id: 'subscribeSelfMatch'
        }
      ]
    }
  ];

  const filterMethods = useForm<RosterPlanFiltersForm>({
    defaultValues: EMPTY_ROSTER_PLAN_FILTERS
  });

  const { mutateAsync: savePreference } = useSavePreferences();

  const userPreferencesInSession = JSON.parse(
    sessionStorage.getItem('userPreferences') || '{}'
  );

  const userPreferences = useReduxSelector(
    (state: RootState) => state.preferences
  );

  const {
    dutiesFromOtherCostCenters,
    employeeCounters,
    rosterEmployeeGridSourceCostCenter,
    rosterEmployeeGridDestinationCostCenter,
    rosterEmployeeNameDisplay,
    rosterSorting
  } = userPreferences;

  const isSortedBySortOrder = rosterSorting?.some(
    (sort) => sort.realAttr === 'sortorder'
  );

  const [showSortOrderColumn, setShowSortOrderColumn] = useState<boolean>(
    isSortedBySortOrder || false
  );

  //   rosterSorting is as following structured:
  //   [
  //     {
  //         "attribute": "nameCompound",
  //         "descending": false,
  //         "realAttr": "sortorder"
  //     },
  //     {
  //         "attribute": "nameCompound",
  //         "descending": false,
  //         "realAttr": "nameCompound"
  //     }
  // ]

  const [employeeNameFormat, setEmployeeNameFormat] =
    useState<EmployeeNameFormatTypes>(
      rosterEmployeeNameDisplay ?? 'nameCompoundSurname'
    );

  const rosterPlanColumnsVisibility = {
    showCommitmentHours: employeeCounters.includes(1),
    showBalanceWeek: employeeCounters.includes(2) && period === 'week',
    showBalanceMonth: employeeCounters.includes(3),
    showCumulativeBalance: employeeCounters.includes(4),
    showWorkedBalance: employeeCounters.includes(5),
    showLeaveBalance: employeeCounters.includes(6),
    showYearlyLeaveBalance: employeeCounters.includes(7),
    showPlbBalance: employeeCounters.includes(8),
    showPlbYearBalance: employeeCounters.includes(9)
  };

  const [showSourceCostCenter, setShowSourceCostCenter] = useState<boolean>(
    rosterEmployeeGridSourceCostCenter === UserPreferenceValueEnums.Show
  );

  const [showCommitmentCostCenter, setShowCommitmentCostCenter] =
    useState<boolean>(
      rosterEmployeeGridDestinationCostCenter === UserPreferenceValueEnums.Show
    );

  const getFunctionId = ({
    statusCode,
    functionId
  }: {
    statusCode: string;
    functionId: number | string;
  }): string | number => {
    switch (statusCode) {
      case 'rosmt':
        return `${functionId}-roster-match`;
      case 'send':
        return `${functionId}-send-to-flex`;
      case 'park':
        return `${functionId}-parked`;
      case 'self':
        return `${functionId}-self`;
      case 'openForSelfMatch':
        return `${functionId}-openForSelfMatch`;
      case 'self-openForSelfMatch':
        return `${functionId}-self-openForSelfMatch`;
      default:
        return functionId;
    }
  };

  const groupOpenRequestsBasedOnFunction = openRequestsData
    ?.reduce(
      (acc, curr, index) => {
        const isSendToFlex = curr.statusCode === 'send';
        const isRosterMatch = curr.statusCode === 'rosmt';
        const isParked = curr.statusCode === 'park';
        const isSelf = curr.statusCode === 'self';
        const isOpenForSelfMatch =
          curr.employeeCostCenterActions?.length > 0 &&
          curr.employeeCostCenterActions.some(
            (item) => item.action === SelfMatchAction.SUBSCRIBE
          );
        const isSelfAndOpenForSelfMatch = isSelf && isOpenForSelfMatch;

        const functionId = getFunctionId({
          statusCode: isSelfAndOpenForSelfMatch
            ? 'self-openForSelfMatch'
            : isOpenForSelfMatch
            ? 'openForSelfMatch'
            : curr.statusCode,
          functionId: curr.functionId
        });

        const existingGroup = acc.find(
          (group) => group.functionId === functionId
        );
        if (existingGroup) {
          existingGroup.days.push(curr);
        } else {
          acc.push({
            functionId,
            functionName: curr.functionName
              ? curr.functionName
              : `${t('dataGrid.functionWithNoGivenName')}`,
            days: [curr],
            isSendToFlex,
            isOpenForSelfMatch,
            isSelfAndOpenForSelfMatch,
            isRosterMatch,
            isParked,
            isSelf,
            functionColor: curr.functionColor,
            order: 1 + index
          });
        }

        return acc;
      },
      [] as {
        functionName: string;
        functionId: string | number;
        days: typeof openRequestsData;
        isSendToFlex: boolean;
        isOpenForSelfMatch: boolean;
        isSelfAndOpenForSelfMatch: boolean;
        isRosterMatch: boolean;
        isParked: boolean;
        isSelf: boolean;
        functionColor?: string;
        order: number;
      }[]
    )
    .sort((a, b) => {
      const getOrderValue = (item: {
        functionId: string | number;
        order: number;
      }) => {
        if (typeof item.functionId === 'number') return 0;
        if (
          typeof item.functionId === 'string' &&
          item.functionId.includes('-roster-match')
        )
          return 2;
        if (
          typeof item.functionId === 'string' &&
          item.functionId.includes('-send-to-flex')
        )
          return 3;
        return 1;
      };

      const orderA = getOrderValue(a);
      const orderB = getOrderValue(b);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.order - b.order;
    });

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      if (period === 'custom') {
        setSelectedDateRange(Array.isArray(date) ? date : undefined);
      } else {
        setSelectedDate(date as Date | null);
      }
    },
    [period, setSelectedDate, setSelectedDateRange]
  );

  const handlePlanUnitSelected = useCallback((planUnits: PlanUnitMix[]) => {
    setSelectedPlanUnits(planUnits);
  }, []);

  const generateDateRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    const dates: string[] = [];
    let current = start.startOf('day');
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      dates.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'day');
    }
    return dates;
  };

  const datesToDisplay =
    period === 'custom' && selectedDateRange
      ? generateDateRange(
          dayjs(selectedDateRange[0]),
          dayjs(selectedDateRange[1])
        )
      : generateDateRange(startDate, endDate);

  const columns = useGetColumns({
    datesToDisplay,
    period,
    isOpenRequests: false,
    qualificationsByCostCenters: qualificationsByCostCenters || [],
    showSourceCostCenter,
    showCommitmentCostCenter,
    costCenterFromOthersPrefValue: dutiesFromOtherCostCenters,
    rosterPlanColumnsVisibility,
    employeeNameFormat,
    showSortOrderColumn
  });

  const columnsOpenRequest = useGetColumns({
    datesToDisplay,
    period,
    isOpenRequests: true,
    qualificationsByCostCenters: qualificationsByCostCenters || [],
    rosterPlanColumnsVisibility,
    showSortOrderColumn
  });

  useSyncHorizontalScroll(apiTop, apiBottom, [datesToDisplay.length]);

  const mergedGetRowClassName = useCallback(
    (
      params: GridRowParams & {
        indexRelativeToCurrentPage: number;
      }
    ) => {
      const isSendToFlex = params.row?.isSendToFlex;
      const isRosterMatch = params.row?.isRosterMatch;
      const isParked = params.row?.isParked;
      const isSelf = params.row?.isSelf;
      const openForSelfMatch = params.row?.isOpenForSelfMatch;
      const isSelfAndOpenForSelfMatch = params.row?.isSelfAndOpenForSelfMatch;

      // default striped background
      const striped =
        params.indexRelativeToCurrentPage % 2 === 0
          ? 'stripedRow-even'
          : 'stripedRow-odd';

      const flagAsSendToFlex = isSendToFlex ? ' send-to-flex-row' : '';
      const flagAsRosterMatch = isRosterMatch ? ' roster-match-row' : '';
      const flagAsParked = isParked ? ' parked-row' : '';
      const flagAsSelf = isSelf ? ' self-row' : '';
      const flagAsOpenForSelfMatch = openForSelfMatch
        ? ' open-for-self-match-row'
        : '';
      const flagAsSelfAndOpenForSelfMatch = isSelfAndOpenForSelfMatch
        ? ' self-and-open-for-self-match-row'
        : '';

      return `${striped}${flagAsSendToFlex}${flagAsRosterMatch}${flagAsParked}${flagAsSelf}${flagAsOpenForSelfMatch}${flagAsSelfAndOpenForSelfMatch}`.trim();
    },
    []
  );

  // column grouping for week numbers
  const weekGroupings = datesToDisplay.reduce(
    (acc, date) => {
      const weekNumber = dayjs(date).week();
      const groupId = t('dataGrid.weekNumber', { weekNumber });
      if (!acc[groupId]) {
        acc[groupId] = { groupId, children: [] };
      }
      acc[groupId].children.push({ field: date });
      return acc;
    },
    {} as Record<string, { groupId: string; children: { field: string }[] }>
  );

  const columnGroupingModel: GridColumnGroupingModel = [
    ...Object.values(weekGroupings)
  ];

  const datesGroup: GridColumnGroupingModel = [
    {
      groupId: 'functionName',
      headerName: t('dataGrid.functions'),
      children: [{ field: 'functionName' }]
    },
    {
      groupId: 'dates',
      headerName: t('dataGrid.openRequests'),
      children: datesToDisplay.map((dates) => ({ field: dates }))
    },
    {
      groupId: 'commitmentHours',
      headerName: t('dataGrid.commitmentHours'),
      children: [{ field: 'commitmentHours' }]
    },
    {
      groupId: 'metrics',
      headerName: ' ',
      children: [
        { field: 'balance' },
        { field: 'cumulative' },
        { field: 'worked' },
        { field: 'leave' },
        { field: 'year' },
        { field: 'plb' },
        { field: 'plbYear' }
      ]
    }
  ];

  const handleCollapsiblePanel = () => {
    const panel = bottomPanelRef.current;
    if (!panel) return;
    panel.isCollapsed() ? panel.expand() : panel.collapse();
  };

  const handleInfoAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElInfo(event.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorElInfo(null);
  };

  const rosterPopoverOpen = Boolean(anchorElInfo);
  const rosterPopOverInfoId = rosterPopoverOpen
    ? 'roster-info-popover'
    : undefined;

  const saveUserPreferences = async ({ key, value }) => {
    const without = userPreferencesInSession.filter(
      (preference) => preference.key !== key
    );

    sessionStorage.setItem(
      'userPreferences',
      JSON.stringify([...without, { key, value }])
    );

    await savePreference([{ key, value }]);
  };

  // Note: Dummy data for duty chips, remove later!
  // const DUMMY_DUTY_CHIPS = [
  //   { code: 'mr1', color: '#016A66' },
  //   { code: '3678shj', color: '#AE600C' },
  //   { code: 'or2', color: '#4D74F1' },
  //   { code: 'Test', color: '#D72829' },
  //   { code: '5555', color: '#F0DA35' },
  //   { code: 'A03!' }
  // ];

  const handleFilterOpen = () => {
    setIsFilterOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const onApplyFilters = filterMethods.handleSubmit((values) => {
    const nextApplied: RosterPlanFilterSelection[] = FILTER_KEYS.map((key) => {
      const raw = values[key]; // FilterOptionType[] | null
      const valueArray = normalizeToOptionArray(raw); // => FilterOptionType[]
      return { key, value: valueArray };
    }).filter((item) => item.value.length > 0);

    setAppliedFilters(nextApplied);
    handleFilterClose();
  });

  const onClearFilters = () => {
    setAppliedFilters([]);
    handleFilterClose();
    filterMethods.reset();
  };

  useEffect(() => {
    if (!employees && !openRequests) return;

    if (appliedFilters.length === 0) {
      setEmployeesData(employees || []);
      setOpenRequestsData(openRequests || []);
      return;
    }

    let filteredEmployeesData = employees || [];
    let filteredOpenRequestsData = openRequests || [];

    // Todo: Refactor later to a separate file
    appliedFilters.forEach((appliedFilter) => {
      const selectedOptions = normalizeToOptionArray(appliedFilter.value);
      if (selectedOptions.length === 0) return;

      switch (appliedFilter.key) {
        // ----------------- EMPLOYEE GRID -----------------
        case 'costCenter': {
          filteredEmployeesData = filteredEmployeesData.filter((employee) =>
            anyOptionLabelEquals(selectedOptions, employee.customerName)
          );
          break;
        }

        case 'function': {
          filteredEmployeesData = filteredEmployeesData.filter((employee) =>
            anyOptionLabelEquals(
              selectedOptions,
              employee.benefits?.functionName
            )
          );
          break;
        }

        case 'employees': {
          filteredEmployeesData = filteredEmployeesData.filter((employee) =>
            anyOptionLabelEquals(selectedOptions, employee.nameInformal)
          );
          break;
        }

        case 'planned': {
          const wantsPlanned = selectedOptions.some(
            (option) => option.id === 'planned'
          );
          const wantsNotPlanned = selectedOptions.some(
            (option) => option.id === 'notPlanned'
          );

          filteredEmployeesData = filteredEmployeesData.filter((employee) => {
            const hasAnyPlanning = (employee.days?.length ?? 0) > 0;
            return (
              (wantsPlanned && hasAnyPlanning) ||
              (wantsNotPlanned && !hasAnyPlanning)
            );
          });
          break;
        }

        case 'types': {
          filteredEmployeesData = filteredEmployeesData.filter((employee) =>
            (employee.types ?? []).some((employeeTypeId) =>
              anyOptionIdEqualsAsString(selectedOptions, employeeTypeId)
            )
          );
          break;
        }

        case 'qualifications': {
          filteredEmployeesData = filteredEmployeesData.filter((employee) =>
            (employee.qualifications ?? []).some((qualificationId) =>
              anyOptionIdEqualsAsString(selectedOptions, qualificationId)
            )
          );
          break;
        }

        case 'dutyCodes': {
          const wantedLabels = buildLabelSet(selectedOptions);
          filteredEmployeesData = filteredEmployeesData.filter(
            (employee) =>
              employee.days?.some(
                (day) =>
                  day.duties?.some((duty) =>
                    wantedLabels.has(duty.displayDutyCode ?? '')
                  )
              )
          );
          break;
        }

        case 'dutyHourTypes': {
          const wantedLabels = buildLabelSet(selectedOptions);
          filteredEmployeesData = filteredEmployeesData.filter(
            (employee) =>
              employee.days?.some(
                (day) =>
                  day.duties?.some(
                    (duty) =>
                      duty.periods?.some((period) =>
                        wantedLabels.has(period.dutyHourType?.code)
                      )
                  )
              )
          );
          break;
        }

        case 'absences': {
          const wantsAbsencePeriods = selectedOptions.some(
            (option) => option.id === 'absences'
          );
          const wantsLeaveRequests = selectedOptions.some(
            (option) => option.id === 'leaveRequests'
          );

          filteredEmployeesData = filteredEmployeesData.filter((employee) => {
            const hasAbsences = employee.days?.some((day) => !!day.absences);
            const hasLeaveRequests = employee.days?.some(
              (day) => !!day.availabilities
            );
            return (
              (wantsAbsencePeriods && hasAbsences) ||
              (wantsLeaveRequests && hasLeaveRequests)
            );
          });
          break;
        }

        // -------------- OPEN REQUESTS GRID ---------------
        case 'costCenterOpenRequests': {
          filteredOpenRequestsData = filteredOpenRequestsData.filter(
            (request) =>
              anyOptionLabelEquals(selectedOptions, request.costCenterName)
          );
          break;
        }

        case 'functionOpenRequests': {
          filteredOpenRequestsData = filteredOpenRequestsData.filter(
            (request) =>
              anyOptionLabelEquals(selectedOptions, request.functionName)
          );
          break;
        }

        case 'qualificationsOpenRequests': {
          filteredOpenRequestsData = filteredOpenRequestsData.filter(
            (request) =>
              (request.qualificationIds ?? []).some((qualificationId) =>
                anyOptionIdEqualsAsString(selectedOptions, qualificationId)
              )
          );
          break;
        }

        case 'dutyCodesOpenRequests': {
          const wantedLabels = buildLabelSet(selectedOptions);
          filteredOpenRequestsData = filteredOpenRequestsData.filter(
            (request) => wantedLabels.has(request.displayDutyCode)
          );
          break;
        }

        case 'dutyHourTypesOpenRequests': {
          const wantedLabels = buildLabelSet(selectedOptions); // dutyHourType.code
          filteredOpenRequestsData = filteredOpenRequestsData.filter(
            (request) =>
              request.periods?.some((period) =>
                wantedLabels.has(period.dutyHourType?.code)
              )
          );
          break;
        }

        case 'statusOpenRequests': {
          const wantsOpen = selectedOptions.some(
            (option) => option.id === 'open'
          );
          const wantsSendToFlex = selectedOptions.some(
            (option) => option.id === 'sendToFlex'
          );
          const wantsOpenForSelfMatch = selectedOptions.some(
            (option) => option.id === 'openForSelfMatch'
          );
          const wantsSubscribeSelfMatch = selectedOptions.some(
            (option) => option.id === 'subscribeSelfMatch'
          );

          filteredOpenRequestsData = filteredOpenRequestsData.filter(
            (request) => {
              const isOpen =
                request.statusCode === 'open' &&
                !request.employeeCostCenterActions;
              const isSend = request.statusCode === 'send';
              const isOpenSelf = !!request.employeeCostCenterActions?.length;
              const isSubscribedSelf =
                request.statusCode === 'bpm' && request.hasSubscribers;

              return (
                (wantsOpen && isOpen) ||
                (wantsSendToFlex && isSend) ||
                (wantsOpenForSelfMatch && isOpenSelf) ||
                (wantsSubscribeSelfMatch && isSubscribedSelf)
              );
            }
          );
          break;
        }

        default:
          break;
      }
    });

    setEmployeesData(filteredEmployeesData);
    setOpenRequestsData(filteredOpenRequestsData);
  }, [employees, openRequests, appliedFilters]);

  if (!hasReactRosterPlanViewAccess) return <NoAccessPage />;

  return (
    <StyledFlexMatchContainer>
      <ToolbarWrapper
        planUnitSelector={{
          onSelected: handlePlanUnitSelected,
          // The roster plans the displayed period, so its plan unit list has to be scoped to it.
          // On the default interval (today -> tomorrow) a cost centre that only becomes valid later
          // sorts to the bottom and renders as invalid — precisely the one the planner navigated
          // there for. The Dojo roster scoped it the same way: CommonModel appended startDate and
          // endDate from the roster's current interval, which navigation moved.
          interval: {
            startDate: startDate.toDate(),
            endDate: endDate.toDate()
          }
        }}
        actions={[
          // Todo: Refactor later to a separate file
          {
            tooltipLabel: tHeader('today'),
            id: 'roster-plan-today',
            icon: 'calendar-check',
            onClick: () => {
              setSelectedDate(dayjs().toDate());
              setPeriod('day');
            },
            actionDataTourId: 'roster-plan-today',
            alignment: 'left'
          },
          {
            tooltipLabel: tHeader('day'),
            id: 'roster-plan-day',
            icon: 'calendar-day',
            onClick: () => {
              setSelectedDate(dayjs().startOf('week').toDate());
              setPeriod('day');
            },
            actionDataTourId: 'roster-plan-day',
            alignment: 'left'
          },
          {
            tooltipLabel: tHeader('week'),
            id: 'roster-plan-week',
            icon: 'calendar-week',
            onClick: () => {
              setSelectedDate(dayjs().toDate());
              setPeriod('week');
            },
            actionDataTourId: 'roster-plan-week',
            alignment: 'left'
          },
          {
            tooltipLabel: tHeader('month'),
            id: 'roster-plan-month',
            icon: 'calendar-month',
            onClick: () => {
              setSelectedDate(dayjs().toDate());
              setPeriod('month');
            },
            actionDataTourId: 'roster-plan-month',
            alignment: 'left'
          },
          {
            tooltipLabel: tHeader('4Weeks'),
            id: 'roster-plan-4Weeks',
            icon: 'alien', // Todo: add icon to SDB UI later, we do not have it right now
            onClick: () => {
              setSelectedDate(dayjs().startOf('week').toDate());
              setPeriod('fourWeeks');
            },
            rightDivider: true,
            actionDataTourId: 'roster-plan-4Weeks',
            alignment: 'left'
          },
          {
            tooltipLabel: tHeader('filter'),
            id: 'roster-plan-filter',
            icon: 'sliders',
            onClick: handleFilterOpen,
            rightDivider: true,
            actionDataTourId: 'roster-plan-filter',
            alignment: 'right',
            pulsingBadge: {
              isActive: appliedFilters.length > 0,
              content:
                appliedFilters.length > 0 ? appliedFilters.length : undefined
            }
          },
          {
            tooltipLabel: tHeader('rosterInfo'),
            id: 'roster-plan-info',
            icon: 'circle-info',
            onClick: handleInfoAction,
            actionDataTourId: 'roster-plan-info',
            alignment: 'right'
          },
          {
            tooltipLabel: tHeader('viewMode'),
            id: 'roster-plan-view-mode',
            icon: 'eye',
            contextMenu: [
              // Todo: enable view mode options later
              // {
              //   label: VIEW_MODE_META.compact.label,
              //   icon: VIEW_MODE_META.compact.icon,
              //   onClick: () => {},
              //   selected: true
              // },
              // {
              //   label: VIEW_MODE_META.detailed.label,
              //   icon: VIEW_MODE_META.detailed.icon,
              //   onClick: () => {},
              //   selected: false,
              //   dividerAfter: true
              // },
              {
                groupTitle: tCommon('toolbar.details'),
                label: tCommon('toolbar.showSourceCostCenter'),
                onClick: () => {
                  setShowSourceCostCenter((prev) => {
                    const next = !prev;
                    saveUserPreferences({
                      key: UserPreferenceKeysEnums.RosterEmployeeGridSourceCostCenter,
                      value: next
                        ? UserPreferenceValueEnums.Show
                        : UserPreferenceValueEnums.Hide
                    });
                    return next;
                  });
                },
                selected: showSourceCostCenter,
                isCheckable: true
              },
              {
                label: tCommon('toolbar.showCommitmentCostCenter'),
                onClick: () => {
                  setShowCommitmentCostCenter((prev) => {
                    const next = !prev;
                    saveUserPreferences({
                      key: UserPreferenceKeysEnums.RosterEmployeeGridDestinationCostCenter,
                      value: next
                        ? UserPreferenceValueEnums.Show
                        : UserPreferenceValueEnums.Hide
                    });
                    return next;
                  });
                },
                selected: showCommitmentCostCenter,
                isCheckable: true
              },
              {
                label: tCommon('toolbar.showSortOrderColumn'),
                onClick: () => {
                  setShowSortOrderColumn((prev) => !prev);
                },
                selected: showSortOrderColumn,
                isCheckable: true
              }
            ],
            actionDataTourId: 'roster-plan-view-mode',
            alignment: 'right'
          }
        ]}
        // Note: Show when duty chips & copy/paste logic are available.
        // The idea is to copy a chip in a state and paste it to a day in the grid
        // component={{
        //   afterDatePicker: <DutyChip duties={DUMMY_DUTY_CHIPS} />
        // }}
        toolbarFlow={ToolbarFlowEnum.FLEX}
        toolbarDataTourId="roster-plan-header"
        dateNavigation={{
          defaultDate: selectedDate ? dayjs(selectedDate).toDate() : null,
          defaultDateRange: selectedDateRange ?? null,
          options: ['day', 'week', 'fourWeeks', 'month'],
          dataTourId: 'roster-plan-date-navigation',
          allowMenuOptions: true,
          onDateChange: handleSelectedDateChange
        }}
        zenPinned={false} // Todo: update later based on filter open state
      >
        <FormProvider {...filterMethods}>
          <FilterPopper
            isOpen={isFilterOpen}
            filterId="roster-plan-filter"
            getAnchorEl={() => document.getElementById('roster-plan-filter')}
            handleClose={handleFilterClose}
            handleClearFilters={onClearFilters}
            handleApplyFilters={onApplyFilters}
            customZIndex={1200}
          >
            <RosterPlanFilters filters={filters} />
          </FilterPopper>
        </FormProvider>
        {/* Roster Info Action Popover */}
        <RosterInfoPopover
          id={rosterPopOverInfoId}
          open={rosterPopoverOpen}
          anchorElInfo={anchorElInfo}
          handleInfoClose={handleInfoClose}
          rosterPublishDate={rosterPublishDate}
        />

        {/* Top Roster Grid & Open Requests Grid */}
        <StyledPanelsContainersBox>
          <PanelGroup
            direction="vertical"
            style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
          >
            <Panel
              defaultSize={70}
              minSize={15}
              style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              <StyledPanelBox>
                <StyledRosterPlanDataGridPro
                  key={
                    dutiesFromOtherCostCenters ||
                    String(rosterPlanColumnsVisibility) ||
                    'none'
                  }
                  rows={employeesData ?? []}
                  columns={columns}
                  className="roster-grid"
                  apiRef={apiTop}
                  hideFooter
                  getRowId={() => uuidv4()}
                  getRowClassName={mergedGetRowClassName}
                  getRowHeight={(params) => {
                    const days = params?.model?.days ?? [];
                    const dutyCounts = days.map((d) => d?.duties?.length ?? 0);
                    const otherCostCenterCounts = days.map(
                      (d) => d?.otherCostCentersDuties?.length ?? 0
                    );
                    const allDutiesCounts = [
                      ...dutyCounts,
                      ...otherCostCenterCounts
                    ];
                    const maxDuties = Math.max(...allDutiesCounts, 1);
                    return 52 * maxDuties;
                  }}
                  initialState={{
                    pinnedColumns: {
                      left: [
                        GRID_CHECKBOX_SELECTION_COL_DEF.field,
                        'sortorder',
                        'employee'
                      ],
                      right: [
                        GRID_CHECKBOX_SELECTION_COL_DEF.field,
                        'commitmentHours',
                        'balanceWeek',
                        'balance',
                        'cumulative',
                        'worked',
                        'leave',
                        'year',
                        'plb',
                        'plbYear'
                      ]
                    },
                    sorting: {
                      sortModel: [{ field: 'employee', sort: 'asc' }]
                    }
                  }}
                  firstColumnName="employee"
                  columnsWithNoDashedBorder={[
                    'sortorder',
                    'commitmentHours',
                    'balanceWeek',
                    'balance',
                    'cumulative',
                    'worked',
                    'leave',
                    'year',
                    'plb',
                    'plbYear'
                  ]}
                  columnGroupingModel={columnGroupingModel}
                  slotProps={{
                    columnHeaders: { className: 'week-header-row' },
                    noRowsOverlay: {
                      isLoading: isFetchingEmployees
                    },
                    columnMenu: {
                      employeeNameFormat,
                      onSelectEmployeeNameFormat: (
                        format: EmployeeNameFormatTypes
                      ) => {
                        setEmployeeNameFormat(format);
                        saveUserPreferences({
                          key: UserPreferenceKeysEnums.RosterEmployeeNameDisplay,
                          value: format
                        });
                      }
                    }
                  }}
                  slots={{
                    noRowsOverlay: NoDataGridRows,
                    columnMenu: CustomColumnMenu
                  }}
                />
              </StyledPanelBox>
            </Panel>

            <PanelResizeHandle onDoubleClick={handleCollapsiblePanel}>
              <StyledResizeHandleBox>
                <StyledResizeHandleDivider>
                  <Icon type="grip-dots-vertical" color="inherit" />
                </StyledResizeHandleDivider>
              </StyledResizeHandleBox>
            </PanelResizeHandle>

            <Panel
              minSize={15}
              collapsible
              ref={bottomPanelRef}
              style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              <StyledPanelBox>
                <StyledRosterPlanDataGridPro
                  rows={groupOpenRequestsBasedOnFunction ?? []}
                  className="open-requests-grid"
                  columns={columnsOpenRequest}
                  apiRef={apiBottom}
                  getRowId={(row) => row.functionId}
                  hideFooter
                  getRowClassName={mergedGetRowClassName}
                  getRowHeight={(params) => {
                    const days = params?.model?.days ?? [];

                    const groupedByDate = days.reduce(
                      (acc, curr) => {
                        const date = dayjs(curr.date).format('YYYY-MM-DD');
                        if (!acc[date]) {
                          acc[date] = [];
                        }
                        acc[date].push(curr);
                        return acc;
                      },
                      {} as Record<string, typeof days>
                    );

                    const dutyCounts =
                      Object.values(groupedByDate)?.map((date) =>
                        Array.isArray(date) ? date.length : 0
                      ) ?? [];

                    const maxDuties = Math.max(...dutyCounts, 1);
                    return 52 * maxDuties;
                  }}
                  initialState={{
                    pinnedColumns: {
                      left: [
                        GRID_CHECKBOX_SELECTION_COL_DEF.field,
                        'sortorder',
                        'functionName'
                      ],
                      right: [
                        GRID_CHECKBOX_SELECTION_COL_DEF.field,
                        'commitmentHours',
                        'balanceWeek',
                        'balance',
                        'cumulative',
                        'worked',
                        'leave',
                        'year',
                        'plb',
                        'plbYear'
                      ]
                    }
                  }}
                  columnGroupingModel={datesGroup}
                  firstColumnName="functionName"
                  slotProps={{
                    noRowsOverlay: { isLoading: isFetchingOpenRequests }
                  }}
                  slots={{
                    noRowsOverlay: NoDataGridRows
                  }}
                  columnsWithNoDashedBorder={[
                    'sortorder',
                    'commitmentHours',
                    'balanceWeek',
                    'balance',
                    'cumulative',
                    'worked',
                    'leave',
                    'year',
                    'plb',
                    'plbYear'
                  ]}
                />
              </StyledPanelBox>
            </Panel>
          </PanelGroup>
        </StyledPanelsContainersBox>
      </ToolbarWrapper>
    </StyledFlexMatchContainer>
  );
};

export default RosterPlan;
